import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import Text from "@components/CustomText";
import PrimaryButton from "@components/PrimaryButton";
import color from "@constants/color";
import { getGigsHistory } from "@services/api/Gigs";
import HistoryStatTile from "./components/HistoryStatTile";
import HistoryCalendar from "./components/HistoryCalendar";
import {
  getHistoryDayStyle,
  getHistoryLegendStates,
  shiftMonth,
} from "./utils";
import type { GigsHistoryResponse, HistoryDayState } from "./types";
import { GIGS_COPY } from "./copy";

const LEGEND_LABELS: Record<HistoryDayState, string> = {
  UPCOMING_YES: GIGS_COPY.history.legendUpcomingYes,
  AWAITING_OUTCOME: GIGS_COPY.history.legendMarkedYes,
  WORKED: GIGS_COPY.history.legendWorked,
  NO_SHOW: GIGS_COPY.history.legendNoShow,
  OFF: GIGS_COPY.history.legendOff,
  NOT_MARKED: GIGS_COPY.history.legendNotMarked,
};

/** Screen 4 — History. Month-scoped stats + a color-coded month calendar. */
const HistoryScreen: React.FC = () => {
  const [data, setData] = useState<GigsHistoryResponse | null>(null);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // The month a fetch is in flight for — a response for a month the CP has
  // since navigated away from (fast prev/next taps) is discarded.
  const requestedMonthRef = useRef<string | undefined>(undefined);

  const load = useCallback((requestMonth?: string) => {
    requestedMonthRef.current = requestMonth;
    setLoading(true);
    setLoadError(false);
    getGigsHistory(requestMonth)
      .then(({ data: response }) => {
        if (requestedMonthRef.current !== requestMonth) return; // stale
        setData(response);
        setMonth(response.month);
      })
      .catch(() => {
        if (requestedMonthRef.current !== requestMonth) return;
        setLoadError(true);
      })
      .finally(() => {
        if (requestedMonthRef.current !== requestMonth) return;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load(undefined); // defaults to the current IST month server-side
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrevMonth = useCallback(() => {
    if (!month) return;
    load(shiftMonth(month, -1));
  }, [month, load]);

  const handleNextMonth = useCallback(() => {
    if (!month) return;
    load(shiftMonth(month, 1));
  }, [month, load]);

  if (loading && !data) {
    return (
      <View style={styles.centered} testID="gigs-history-loading">
        <ActivityIndicator color={color.primary} size="large" />
      </View>
    );
  }

  if (loadError && !data) {
    return (
      <View style={styles.centered} testID="gigs-history-error">
        <Text variant="body_text_2" style={styles.errorText}>
          {GIGS_COPY.history.loadError}
        </Text>
        <PrimaryButton
          text={GIGS_COPY.history.retry}
          onPress={() => load(undefined)}
        />
      </View>
    );
  }

  if (!data) return null;

  const legendStates = getHistoryLegendStates(data.show_outcomes);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text variant="heading_24" style={styles.title}>
        {GIGS_COPY.history.title}
      </Text>
      <Text variant="body_text_2" style={styles.subtitle}>
        {GIGS_COPY.history.subtitle}
      </Text>

      <View style={styles.tiles}>
        <HistoryStatTile
          value={data.stats.marked_yes}
          label={GIGS_COPY.history.markedYes}
        />
        {data.show_outcomes ? (
          <>
            <HistoryStatTile
              value={data.stats.worked}
              label={GIGS_COPY.history.worked}
            />
            <HistoryStatTile
              value={data.stats.no_show}
              label={GIGS_COPY.history.noShow}
            />
          </>
        ) : null}
      </View>

      <HistoryCalendar
        month={data.month}
        minMonth={data.min_month}
        maxMonth={data.max_month}
        days={data.days}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />

      <View style={styles.legend}>
        {legendStates.map((state) => {
          const cellStyle = getHistoryDayStyle(state);
          // Built outside the JSX: a swatch's colours depend on its state, so
          // they can't live in the StyleSheet.
          const swatchStyle = {
            backgroundColor: cellStyle.backgroundColor,
            borderStyle: cellStyle.dashed
              ? ("dashed" as const)
              : ("solid" as const),
            borderWidth: cellStyle.dashed ? 1.5 : 1,
            borderColor: cellStyle.borderColor ?? color.lineColor,
          };
          return (
            <View key={state} style={styles.legendItem}>
              <View style={[styles.legendSwatch, swatchStyle]} />
              <Text variant="caption_2" style={styles.legendLabel}>
                {LEGEND_LABELS[state]}
              </Text>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

export default HistoryScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 16,
  },
  errorText: {
    color: color.mediumBlack,
    textAlign: "center",
  },
  title: {
    color: color.mediumBlack,
  },
  subtitle: {
    color: color.lightMediumBlack,
    marginTop: 4,
  },
  tiles: {
    flexDirection: "row",
    gap: 10,
    marginTop: 20,
  },
  legend: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
    marginTop: 16,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },
  legendLabel: {
    color: color.lightMediumBlack,
  },
});
